/**
 * Small editorial illustrations for PagePledge. Flat colour, bookbinding
 * details, and a shared hierarchy of fine outlines and lighter interior marks.
 * Keep silhouettes legible at the 60–100px sizes used in the page margins.
 */
const P = {
  ink: "hsl(var(--foreground))",
  paper: "hsl(var(--card))",
  edge: "hsl(var(--secondary))",
  clay: "hsl(var(--primary))",
  forest: "hsl(var(--accent))",
  amber: "hsl(var(--warning))",
  sky: "hsl(var(--info))",
};
const outline = { stroke: P.ink, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
const detail = { ...outline, strokeWidth: 1, strokeOpacity: 0.35 };

function Drawing({ width, height, size = width, rotate = 0, className = "", children }) {
  return (
    <svg viewBox={`0 0 ${width} ${height}`} width={size} height={size * height / width}
      xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"
      className={className} style={rotate ? { transform: `rotate(${rotate}deg)` } : undefined} fill="none">
      {children}
    </svg>
  );
}

function Spark({ x, y, size = 5, color = P.clay }) {
  return <path d={`M${x} ${y-size} Q${x} ${y} ${x+size} ${y} Q${x} ${y} ${x} ${y+size} Q${x} ${y} ${x-size} ${y} Q${x} ${y} ${x} ${y-size}Z`} fill={color} />;
}

// A gentle curve gives the pages a recognisable gutter, with a separate
// cover and page block. This drawing is shared by ornaments and vignettes.
function BookSpread({ cover = P.forest }) {
  return (
    <g {...outline}>
      <path d="M8 23Q29 16 50 26Q71 16 92 23L94 66Q72 60 51 71H49Q28 60 6 66Z" fill={cover} />
      <path d="M11 19Q31 14 50 24Q69 14 89 19L90 61Q69 56 50 67Q31 56 10 61Z" fill={P.edge} />
      <path d="M13 14Q33 12 50 24V63Q33 51 13 55Z" fill={P.paper} />
      <path d="M50 24Q66 12 87 14V55Q67 51 50 63Z" fill={P.paper} />
      <path d="M45 22Q46 41 47 57M55 22Q54 41 53 57" {...detail} />
      <path d="M20 26Q31 25 40 30M20 33Q31 32 40 37M20 40Q29 39 36 43M61 30Q71 25 80 26M61 37Q71 32 80 33M61 44Q70 40 76 40" {...detail} />
      <path d="M70 15L76 14V36L73 33L70 37Z" fill={P.clay} strokeWidth="1" />
      <path d="M14 59Q32 56 46 64M54 64Q69 56 86 59" {...detail} />
    </g>
  );
}

export function OpenBookDoodle({ size = 80, rotate = 0, className = "" }) {
  return (
    <Drawing width={100} height={80} size={size} rotate={rotate} className={className}>
      <BookSpread />
    </Drawing>
  );
}

export function ClosedBookDoodle({ size = 70, rotate = -8, color = "clay", className = "" }) {
  const cover = P[color] || (color === "cream" ? P.edge : P.clay);
  return (
    <Drawing width={80} height={100} size={size} rotate={rotate} className={className}>
      <g {...outline}>
        <path d="M14 15L61 8Q65 7 65 12L67 77L22 89Q13 91 12 83L10 24Q10 18 14 15Z" fill={cover} />
        <path d="M20 77L66 69V82L23 90Q15 91 15 84Q15 79 20 77Z" fill={P.paper} />
        <path d="M22 81L61 74M22 85L61 78" {...detail} />
        <path d="M10 22Q10 16 17 15L62 7L64 71L20 79Q14 80 14 85L10 25Z" fill={cover} />
        <path d="M18 16L22 78" strokeOpacity="0.4" strokeWidth="1" />
        <path d="M24 24L55 18L57 61L27 67Z" stroke={P.paper} strokeWidth="0.9" strokeOpacity="0.65" />
        <path d="M35 78L43 76V94L39 91L35 95Z" fill={P.amber} strokeWidth="1" />
      </g>
      <g transform="translate(40 42) rotate(-9)" stroke={P.paper} strokeWidth="1.2" strokeLinecap="round">
        <path d="M0 12V-10M0 4C-9 4-10-1-10-4C-3-4 0-1 0 4M0-3C8-3 9-7 9-10C3-10 0-7 0-3" />
        <path d="M-5 16H5M-3-15H3" strokeOpacity="0.7" />
      </g>
    </Drawing>
  );
}

function StackedBook({ x, y, width, cover, lean = 0 }) {
  return (
    <g transform={`rotate(${lean} ${x + width / 2} ${y + 10})`} {...outline}>
      <path d={`M${x+7} ${y+3}H${x+width-2}V${y+19}H${x+7}Q${x} ${y+19} ${x} ${y+11}Q${x} ${y+3} ${x+7} ${y+3}Z`} fill={P.paper} />
      <path d={`M${x+12} ${y+8}H${x+width-8}M${x+12} ${y+13}H${x+width-6}`} {...detail} />
      <path d={`M${x+width} ${y+1}H${x+7}Q${x-3} ${y+1} ${x-3} ${y+11}Q${x-3} ${y+21} ${x+7} ${y+21}H${x+width}V${y+17}H${x+8}Q${x+3} ${y+17} ${x+3} ${y+11}Q${x+3} ${y+5} ${x+8} ${y+5}H${x+width}Z`} fill={cover} />
    </g>
  );
}

export function SmallStackDoodle({ size = 90, rotate = 3, className = "" }) {
  return (
    <Drawing width={120} height={90} size={size} rotate={rotate} className={className}>
      <StackedBook x={15} y={58} width={91} cover={P.forest} />
      <StackedBook x={24} y={34} width={79} cover={P.clay} lean={-4} />
      <StackedBook x={20} y={10} width={84} cover={P.amber} lean={3} />
      <path d="M82 17L90 18L88 44L84 40L79 43Z" fill={P.clay} {...outline} strokeWidth="1" />
    </Drawing>
  );
}

export function FlyingPagesDoodle({ size = 90, rotate = -5, className = "" }) {
  return (
    <Drawing width={110} height={110} size={size} rotate={rotate} className={className}>
      <path d="M17 59C5 42 20 21 39 24M89 56C107 44 100 20 83 15" {...detail} strokeDasharray="3 5" />
      <g transform="translate(5 40) scale(1 .88)"><BookSpread cover={P.clay} /></g>
      <g {...outline}>
        <path d="M28 21Q43 15 54 23L47 53Q39 44 23 47Q30 34 28 21Z" fill={P.paper} />
        <path d="M65 8Q77 14 91 11Q82 27 87 42Q75 44 61 35Q67 21 65 8Z" fill={P.paper} />
        <path d="M34 27Q40 25 47 28M32 33Q39 31 45 34M71 18L81 21M69 24L79 27M68 30L75 32" {...detail} />
      </g>
      <Spark x={16} y={17} size={4} color={P.amber} />
    </Drawing>
  );
}

export function PencilDoodle({ size = 70, rotate = 25, className = "" }) {
  return (
    <Drawing width={100} height={30} size={size} rotate={rotate} className={className}>
      <g {...outline} strokeWidth="1.3">
        <path d="M7 15L25 8H78V22H25Z" fill={P.amber} />
        <path d="M7 15L25 8L22 13L25 17L25 22Z" fill={P.edge} />
        <path d="M7 15L14 12.3L14 17.7Z" fill={P.ink} stroke="none" />
        <path d="M25 17H78V22H25Z" fill={P.clay} fillOpacity="0.2" stroke="none" />
        <path d="M28 11H75" stroke={P.paper} strokeOpacity="0.65" strokeWidth="1" />
        <path d="M78 8H85V22H78Z" fill={P.edge} />
        <path d="M81 10V20M83 10V20" {...detail} />
        <path d="M85 8H90Q95 8 95 13V17Q95 22 90 22H85Z" fill={P.clay} />
        <path d="M88 11H91" stroke={P.paper} strokeOpacity="0.55" strokeWidth="1" />
      </g>
    </Drawing>
  );
}

// Feature vignettes use the same drawing language, with enough room for
// a clear focal object and one supporting cue rather than scattered confetti.
export function StepImportIllustration({ className = "" }) {
  return (
    <Drawing width={220} height={140} className={className}>
      <path d="M37 109H185" {...detail} />
      <g transform="rotate(-5 95 68)" {...outline}>
        <rect x="42" y="23" width="116" height="98" rx="5" fill={P.edge} />
        <rect x="49" y="16" width="116" height="98" rx="5" fill={P.paper} />
        <path d="M54 16H160Q165 16 165 21V39H49V21Q49 16 54 16Z" fill={P.forest} />
        <path d="M62 27H101" stroke={P.paper} strokeWidth="2" />
        <path d="M137 25V30M144 25V30M151 25V30" stroke={P.paper} strokeWidth="1.2" strokeOpacity="0.6" />
        {[54, 74, 94].map((y, i) => (
          <g key={y}>
            <circle cx="64" cy={y} r="4" fill={[P.clay, P.amber, P.sky][i]} stroke="none" />
            <path d={`M76 ${y-2}H107M76 ${y+3}H96`} {...detail} />
            <path d={`M120 ${y+1}L123 ${y+4}L129 ${y-3}`} stroke={P.forest} strokeWidth="1.4" />
            <path d={`M58 ${y+10}H153`} {...detail} strokeOpacity="0.15" />
          </g>
        ))}
      </g>
      <g {...outline}>
        <circle cx="170" cy="100" r="21" fill={P.forest} />
        <path d="M170 108V91M163 98L170 91L177 98M160 110V114H180V110" stroke={P.paper} strokeWidth="1.8" />
      </g>
      <Spark x={184} y={28} size={6} color={P.clay} />
    </Drawing>
  );
}

export function StepConnectPaymentsIllustration({ className = "" }) {
  return (
    <Drawing width={220} height={140} className={className}>
      <path d="M31 121H191" {...detail} />
      <g transform="rotate(-6 95 68)" {...outline}>
        <rect x="38" y="28" width="131" height="86" rx="9" fill={P.edge} />
        <rect x="38" y="22" width="131" height="86" rx="9" fill={P.paper} />
        <path d="M38 43H169V61H38Z" fill={P.sky} stroke="none" />
        <rect x="52" y="73" width="23" height="17" rx="4" fill={P.amber} />
        <path d="M60 73V90M67 73V90M52 81H75" {...detail} />
        <path d="M90 80H114M90 88H132" {...detail} />
      </g>
      <g transform="translate(140 75)">
        <circle cx="27" cy="27" r="27" fill={P.paper} {...outline} />
        <g transform="translate(6 6) scale(1.75)" stroke={P.forest} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </g>
      </g>
      <Spark x={184} y={29} size={6} color={P.amber} />
    </Drawing>
  );
}

function PaperCode() {
  return (
    <g fill={P.ink}>
      {[[0, 0], [24, 0], [0, 24]].map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <rect x={x} y={y} width="16" height="16" rx="1" />
          <rect x={x+3} y={y+3} width="10" height="10" fill={P.paper} />
          <rect x={x+6} y={y+6} width="4" height="4" />
        </g>
      ))}
      <path d="M19 0H22V10H19ZM0 19H10V22H0ZM19 15H24V21H19ZM28 19H34V24H28ZM37 18H40V31H37ZM19 28H24V40H19ZM27 28H32V33H27ZM28 37H40V40H28ZM34 31H37V37H34Z" />
    </g>
  );
}

export function StepTakeHomeIllustration({ className = "" }) {
  return (
    <Drawing width={220} height={140} className={className}>
      <g transform="rotate(7 123 67)" {...outline}>
        <rect x="83" y="18" width="86" height="105" rx="4" fill={P.edge} />
        <path d="M98 32H151M98 40H139" {...detail} />
      </g>
      <g transform="rotate(-5 100 67)" {...outline}>
        <path d="M56 16H121L141 36V121H56Z" fill={P.paper} />
        <path d="M121 16V36H141Z" fill={P.edge} />
        <path d="M68 32H105" stroke={P.clay} strokeWidth="3" />
        <path d="M68 40H113" {...detail} />
        <g transform="translate(78 52)" stroke="none"><PaperCode /></g>
        <path d="M76 105H119" stroke={P.forest} strokeWidth="2" strokeDasharray="8 3 8 3 8" />
      </g>
      <g {...outline}>
        <circle cx="171" cy="99" r="18" fill={P.amber} />
        <path d="M160 99L168 106L182 91" strokeWidth="2" />
      </g>
      <Spark x={37} y={34} size={5} color={P.forest} />
    </Drawing>
  );
}

export function StepLogMinutesIllustration({ className = "" }) {
  return (
    <Drawing width={220} height={140} className={className}>
      <g transform="translate(20 28) rotate(-5 75 50) scale(1.5 1.25)"><BookSpread /></g>
      <g {...outline}>
        <path d="M169 24V17M162 16H176M188 31L193 26" strokeWidth="2" />
        <circle cx="169" cy="55" r="29" fill={P.clay} />
        <circle cx="169" cy="55" r="23" fill={P.paper} strokeWidth="1" />
        <path d="M169 36V39M188 55H185M169 74V71M150 55H153" {...detail} />
        <path d="M169 41V55L179 61" strokeWidth="1.8" />
        <circle cx="169" cy="55" r="2" fill={P.clay} stroke="none" />
      </g>
      <Spark x={33} y={29} size={5} color={P.amber} />
    </Drawing>
  );
}

export function HeroBookStack({ className = "" }) {
  return (
    <Drawing width={320} height={340} size={280} className={className}>
      <g transform="translate(12 104) scale(2.5)"><SmallStackDoodle size={120} rotate={0} /></g>
      <g transform="translate(190 22) rotate(12 40 50)"><ClosedBookDoodle size={90} rotate={0} color="forest" /></g>
      <Spark x={77} y={67} size={12} color={P.clay} />
      <path d="M34 318H285" {...detail} />
    </Drawing>
  );
}

export function HeroScatter({ className = "" }) {
  return (
    <svg viewBox="0 0 1440 720" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" className={className} preserveAspectRatio="xMidYMid slice" fill="none">
      <Spark x={72} y={124} size={9} color={P.clay} />
      <Spark x={1380} y={205} size={7} color={P.forest} />
      <path d="M30 420Q80 370 104 410M1354 547Q1380 578 1410 555" {...detail} strokeWidth="1.5" />
    </svg>
  );
}

export function HighlighterSwipe({ color = P.amber, className = "" }) {
  return (
    <svg viewBox="0 0 200 40" preserveAspectRatio="none" className={className} aria-hidden="true" focusable="false" fill="none">
      <path d="M3 10Q72 5 197 8L195 32Q107 29 4 35Z" fill={color} opacity="0.3" />
      <path d="M7 32Q99 28 192 31" stroke={color} strokeWidth="3" opacity="0.16" />
    </svg>
  );
}

export function SectionDivider({ variant = "star", className = "" }) {
  return (
    <div className={`flex items-center gap-4 text-primary ${className}`}>
      <span className="flex-1 h-px bg-border" />
      <Drawing width={40} height={40} size={32}>
        {variant === "star" && <Spark x={20} y={20} size={9} />}
        {variant === "book" && <g transform="translate(1 4) scale(.38)"><BookSpread /></g>}
        {variant === "dot" && <g fill={P.clay}><circle cx="14" cy="20" r="2" /><circle cx="22" cy="20" r="3" /></g>}
      </Drawing>
      <span className="flex-1 h-px bg-border" />
    </div>
  );
}
